import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useOrg } from "../../lib/org";
import { Card, ErrorState, Money, PageHeader, Spinner } from "../../components/ui";
import { yearStart, today } from "../../lib/format";
import { DateInput, ReportControls, ReportSectionTable } from "./controls";
import type { ProfitAndLoss as PnL } from "@shared/types";

export default function ProfitAndLoss() {
  const { currentOrg } = useOrg();
  const currency = currentOrg?.baseCurrency ?? "USD";
  const [start, setStart] = useState(yearStart());
  const [end, setEnd] = useState(today());

  const { data, isLoading, error } = useQuery({
    queryKey: ["pnl", currentOrg?.id, start, end],
    queryFn: () =>
      api.get<PnL>(`/reports/profit-and-loss?start=${start}&end=${end}`),
    enabled: !!currentOrg,
  });

  return (
    <div>
      <PageHeader title="Profit & Loss" subtitle={`${start} → ${end}`} />
      <ReportControls>
        <DateInput label="From" value={start} onChange={setStart} />
        <DateInput label="To" value={end} onChange={setEnd} />
      </ReportControls>

      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <Card className="p-6">
          <ReportSectionTable
            title="Income"
            rows={data.income.lines}
            total={data.income.total}
            currency={currency}
          />
          {data.cogs.lines.length > 0 && (
            <ReportSectionTable
              title="Cost of Goods Sold"
              rows={data.cogs.lines}
              total={data.cogs.total}
              currency={currency}
            />
          )}
          <SummaryRow label="Gross Profit" cents={data.grossProfit} currency={currency} />
          <ReportSectionTable
            title="Expenses"
            rows={data.expenses.lines}
            total={data.expenses.total}
            currency={currency}
          />
          <SummaryRow
            label="Net Operating Income"
            cents={data.netOperatingIncome}
            currency={currency}
          />
          <div className="mt-4 flex items-center justify-between border-t-2 border-gray-300 pt-3 text-lg font-bold">
            <span>Net Income</span>
            <Money cents={data.netIncome} currency={currency} />
          </div>
        </Card>
      )}
    </div>
  );
}

function SummaryRow({
  label,
  cents,
  currency,
}: {
  label: string;
  cents: number;
  currency: string;
}) {
  return (
    <div className="my-2 flex items-center justify-between rounded bg-gray-50 px-3 py-2 font-semibold">
      <span>{label}</span>
      <Money cents={cents} currency={currency} />
    </div>
  );
}
